import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { Reveal } from "../lib/motion";
import { MascotTip } from "../components/Mascot";
import { useRequestDialog } from "../components/RequestDialog";
import { usePageMeta } from "../lib/seo";
import { api } from "../lib/api";
import { SERVICES } from "../content/defaults";

export function BlogList() {
  usePageMeta("Electrical Tips & Vehicle Guides | C.C.C. Auto Electric",
    "Practical battery, wiring and starting guides by make, model and year — reviewed by the C.C.C. Auto Electric shop staff.");
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    api("/blog").then((d) => setPosts(d.posts)).catch(() => setPosts([]));
  }, []);
  return (
    <main>
      <section className="hero" style={{ paddingBottom: 24 }}>
        <div className="container">
          <Reveal>
            <span className="eyebrow red">Electrical tips & vehicle guides</span>
            <h1 style={{ fontSize: "clamp(30px,4.4vw,54px)", lineHeight: 1.05, margin: "16px 0 14px" }}>Helpful tips for your make, model and year.</h1>
            <p className="hero-sub">Practical symptom guides written for owners — reviewed by shop staff, with manufacturer sources. No invented success stories, no unverified claims.</p>
          </Reveal>
        </div>
      </section>
      <section className="section" style={{ paddingTop: 20 }}>
        <div className="container">
          {posts === null && <div className="skeleton" style={{ height: 200 }} />}
          <div style={{ display: "grid", gap: 18, gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }} data-testid="blog-list">
            {posts && posts.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.06}>
                <Link to={`/blog/${p.slug}`} style={{ textDecoration: "none", display: "block", height: "100%" }} data-testid={`blog-card-${p.slug}`}>
                  <div className="card" style={{ padding: 26, height: "100%", transition: "transform .25s, border-color .25s" }}>
                    <span className="eyebrow red">{[p.year, p.make, p.model].filter(Boolean).join(" ") || "Guide"}</span>
                    <h3 style={{ fontSize: 19, margin: "12px 0 10px", lineHeight: 1.3 }}>{p.title}</h3>
                    <p className="muted" style={{ fontSize: 14, lineHeight: 1.65, margin: 0 }}>{p.intro}</p>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 16, color: "var(--blue-bright)", fontSize: 13.5, fontWeight: 600 }}>
                      Read the guide <ExternalLink size={13} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <div style={{ marginTop: 40 }}><MascotTip /></div>
        </div>
      </section>
    </main>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);
  const openDialog = useRequestDialog();

  usePageMeta(post ? `${post.title} | C.C.C. Auto Electric` : "Loading article… | C.C.C. Auto Electric",
    post ? post.intro : "Practical automotive electrical guides by make, model and year.");

  useEffect(() => {
    api(`/blog/${slug}`).then(setPost).catch(() => setMissing(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (missing) {
    return (
      <main className="section container" style={{ textAlign: "center" }}>
        <h2>That article is not available.</h2>
        <Link to="/blog" className="btn btn-ghost" style={{ marginTop: 16 }}>Back to the blog</Link>
      </main>
    );
  }
  if (!post) return <main className="section container"><div className="skeleton" style={{ height: 300 }} /></main>;

  const svc = SERVICES.find((s) => s.id === post.service);

  return (
    <main>
      <section className="hero" style={{ paddingBottom: 16 }}>
        <div className="container" style={{ maxWidth: 820 }}>
          <Reveal>
            <span className="eyebrow red">{[post.year, post.make, post.model].filter(Boolean).join(" ")}</span>
            <h1 style={{ fontSize: "clamp(28px,4vw,48px)", lineHeight: 1.08, margin: "16px 0 16px" }} data-testid="blog-post-title">{post.title}</h1>
            <p className="hero-sub">{post.intro}</p>
          </Reveal>
        </div>
      </section>
      <section className="section" style={{ paddingTop: 26 }}>
        <div className="container" style={{ maxWidth: 820, display: "grid", gap: 22 }}>
          {post.sections.map((s, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <div className="card" style={{ padding: 28 }}>
                <h2 style={{ fontSize: 20, marginBottom: 10 }}>{s.heading}</h2>
                <p className="muted" style={{ margin: 0, lineHeight: 1.75, fontSize: 15 }}>{s.text}</p>
              </div>
            </Reveal>
          ))}
          {post.source_url && (
            <p className="form-note">
              Manufacturer source: <a href={post.source_url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--blue-bright)" }}>{post.source_url}</a>
            </p>
          )}
          <Reveal>
            <div className="card" style={{ padding: 30, textAlign: "center" }}>
              <h3 style={{ fontSize: 20, marginBottom: 10 }}>Seeing these symptoms on your {post.year || ""} {post.make || "vehicle"}?</h3>
              <p className="muted" style={{ marginBottom: 20 }}>Send the details online — the shop confirms availability before any visit.</p>
              <button className="btn btn-red" onClick={() => openDialog(svc ? svc.primary.service : "Electrical diagnosis", "", true)} data-testid="blog-post-cta">
                {svc ? svc.primary.label : "Request an Appointment"}
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
